<template>
    <div>
        <template v-if="!isAdmin">
            <PermissionDenied />
        </template>
        <template v-else>
            <vs-tabs v-model="activeTab">
                <vs-tab label="List" icon-pack="feather" icon="icon-list">
                    <div class="mt-6">
                        <!-- Table Section Header -->
                        <vs-row class="mb-4">
                            <vs-col vs-w="6">
                                <h4 class="mt-2">Item Sub Group List</h4>
                            </vs-col>
                            <vs-col vs-w="6" class="text-right flex justify-end">
                                <vs-button v-if="canCreate" color="primary" type="filled" @click="activeTab = 1; resetForm();" icon-pack="feather" icon="icon-plus" class="mr-2">Add New</vs-button>
                                <vx-tooltip text="Refresh Data" color="primary">
                                    <vs-button color="primary" type="filled" @click="loadData" :disabled="loading">
                                        <font-awesome-icon :icon="redoIcon" />
                                    </vs-button>
                                </vx-tooltip>
                            </vs-col>
                        </vs-row>

                        <!-- Search and Filter Row -->
                        <vs-row class="mb-3">
                            <vs-col vs-xs="12" vs-sm="12" vs-lg="4" class="px-0 pr-2 mb-2">
                                <vs-input
                                    class="w-full"
                                    placeholder="Search by code or description..."
                                    v-model="filters.search"
                                />
                            </vs-col>
                            <vs-col vs-xs="12" vs-sm="6" vs-lg="4" class="px-0 pr-2 mb-2">
                                <SelectForeign
                                    :model.sync="filters.filter_item_group"
                                    label=""
                                    placeholder="Filter by Item Group"
                                    :multiple="false"
                                    :load_initial="true"
                                    :label_keys="['code']"
                                    :show_id_in_label="false"
                                    :endpoint="itemGroupEndpoint"
                                    container_class="w-full"
                                    input_class=""
                                    :use_id_as_value="true"
                                    :result_filter="item => item.is_active"
                                />
                            </vs-col>
                            <vs-col vs-xs="12" vs-sm="6" vs-lg="4" class="px-0 mb-2">
                                <InputSelect
                                    :model.sync="filters.filter_status"
                                    label=""
                                    placeholder="Filter by Status"
                                    container_class="w-full"
                                    :options="status_options"
                                />
                            </vs-col>
                        </vs-row>
                        
                        <vx-card>
                            <div v-if="loading" class="text-center p-4">
                                <h3>Loading Data.. Please wait..</h3>
                            </div>
                            
                            <vs-table :data="results" slot="no-body" :hoverFlat="true" stripe v-if="!loading" noDataText="">
                                <vs-tr>
                                    <vs-th style="min-width: 80px;">S.No</vs-th>
                                    <vs-th style="min-width: 150px;">Item Group Code</vs-th>
                                    <vs-th style="min-width: 200px;">Item Group Description</vs-th>
                                    <vs-th style="min-width: 150px;">Item Sub Group Code</vs-th>
                                    <vs-th style="min-width: 200px;">Item Sub Group Description</vs-th>
                                    <vs-th style="min-width: 120px;">UOM</vs-th>
                                    <vs-th style="min-width: 180px;">UOM Description</vs-th>
                                    <vs-th style="min-width: 120px;">Status</vs-th>
                                    <vs-th style="min-width: 100px;" v-if="canEdit">Action</vs-th>
                                </vs-tr>
                                <template v-if="results.length > 0">
                                    <template v-for="(item, index) in results">
                                        <vs-tr :key="index">
                                            <vs-td>
                                                {{ filters.offset + index + 1 }}
                                            </vs-td>
                                            <vs-td>
                                                {{ getItemGroupCode(item.item_group) || '-' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ getItemGroupDescription(item.item_group) || '-' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ item.code || '-' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ item.description || '-' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ getUOMCode(item.uom) || '-' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ getUOMDescription(item.uom) || '-' }}
                                            </vs-td>
                                            <vs-td>
                                                <span :class="item.is_active ? 'text-success' : 'text-danger'">
                                                    {{ item.is_active ? 'Active' : 'Inactive' }}
                                                </span>
                                            </vs-td>
                                            <vs-td v-if="canEdit">
                                                <vs-button @click="editItem(item)" color="primary" type="border" icon="icon-edit" icon-pack="feather" size="small"></vs-button>
                                            </vs-td>
                                        </vs-tr>
                                    </template>
                                </template>
                                <template v-else>
                                    <vs-tr>
                                        <vs-td :colspan="canEdit ? 9 : 8" class="text-center p-4">
                                            <p class="text-muted mb-0" v-if="has_active_filters">No results found for the current search/filter criteria.</p>
                                            <p class="text-muted mb-0" v-else>No data available</p>
                                        </vs-td>
                                    </vs-tr>
                                </template>
                            </vs-table>
                        </vx-card>
                        
                        <!-- Pagination -->
                        <template v-if="total_results">
                            <vs-row class="mt-3 text-center">
                                <vs-pagination :total="total_pages" v-model="current_page" goto class="mx-auto"></vs-pagination>
                            </vs-row>
                        </template>
                    </div>
                </vs-tab>

                <vs-tab v-if="canCreate || canEdit" :label="editingId ? 'Edit' : 'Add'" icon-pack="feather" :icon="editingId ? 'icon-edit' : 'icon-plus'">
                    <div class="mt-6">
                        <!-- Form Section -->
                        <vx-card noShadow noRound>
                            <div class="mb-4">
                                <h3 class="mb-2">{{ editingId ? 'Edit Item Sub Group' : 'Create Item Sub Group' }}</h3>
                                <p class="text-muted mb-4" v-if="!editingId">Fill in the details below to create a new item sub group entry.</p>
                                <p class="text-muted mb-4" v-else>Update the item sub group details below.</p>
                            </div>
                            
                            <vs-row>
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <SelectForeign
                                        :model.sync="formData.item_group"
                                        label="Item Group Code *"
                                        placeholder="Type to Search..."
                                        :multiple="false"
                                        :load_initial="true"
                                        :label_keys="['code']"
                                        :show_id_in_label="false"
                                        :endpoint="itemGroupEndpoint"
                                        container_class="w-full"
                                        input_class=""
                                        :use_id_as_value="true"
                                        :close_on_select="true"
                                        :result_filter="item => item.is_active"
                                        @change="updateItemGroupDescription"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.item_group">
                                        {{ (api_errors.item_group || []).join(', ') }}
                                    </small>
                                </vs-col>
                                
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <vs-input
                                        v-model="itemGroupDescription"
                                        label="Item Group Description"
                                        placeholder="Auto-filled from selection"
                                        class="w-full"
                                        disabled
                                    />
                                </vs-col>
                                
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <vs-input
                                        v-model="formData.code"
                                        label="Item Sub Group Code *"
                                        placeholder="S001"
                                        class="w-full"
                                        :disabled="true"
                                    />
                                    <small class="block text-warning mt-1">
                                        Note: Auto generated
                                    </small>
                                    <small class="block text-danger mt-1" v-if="api_errors.code">
                                        {{ (api_errors.code || []).join(', ') }}
                                    </small>
                                </vs-col>
                                
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <vs-input
                                        v-model="formData.description"
                                        label="Item Sub Group Description *"
                                        placeholder="e.g., Milling"
                                        class="w-full"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.description">
                                        {{ (api_errors.description || []).join(', ') }}
                                    </small>
                                </vs-col>
                            </vs-row>
                            
                            <vs-row>
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <SelectForeign
                                        :model.sync="formData.uom"
                                        label="Item UOM *"
                                        placeholder="Type to Search..."
                                        :multiple="false"
                                        :load_initial="true"
                                        :label_keys="['code']"
                                        :show_id_in_label="false"
                                        :endpoint="uomEndpoint"
                                        container_class="w-full"
                                        input_class=""
                                        :use_id_as_value="true"
                                        :close_on_select="true"
                                        :result_filter="item => item && (item.is_active === true || item.is_active === 1 || item.is_active === '1' || String(item.is_active).toLowerCase() === 'true')"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.uom">
                                        {{ (api_errors.uom || []).join(', ') }}
                                    </small>
                                </vs-col>
                                
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <label class="vs-input--label block mb-2">Status *</label>
                                    <div class="flex align-center">
                                        <vs-radio v-model="formData.is_active" vs-name="is_active" :vs-value="true" class="mr-4">Active</vs-radio>
                                        <vs-radio v-model="formData.is_active" vs-name="is_active" :vs-value="false">Inactive</vs-radio>
                                    </div>
                                    <small class="block text-danger mt-1" v-if="api_errors.is_active">
                                        {{ (api_errors.is_active || []).join(', ') }}
                                    </small>
                                </vs-col>
                                
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                                    <label class="vs-input--label block mb-2">DRP Input Format</label>
                                    <vs-button @click="openDRPDialog" color="primary" type="filled" class="w-full">
                                        <font-awesome-icon :icon="settingsIcon" class="mr-2" />
                                        Configure DRP Input Format
                                    </vs-button>
                                </vs-col>
                            </vs-row>
                            
                            <vs-row class="mt-4">
                                <vs-col vs-xs="12" vs-sm="12" vs-lg="12" class="text-right">
                                    <vs-button @click="resetForm(); activeTab = 0;" color="warning" type="flat" class="mr-2" v-if="editingId">
                                        Cancel
                                    </vs-button>
                                    <vs-button @click="save" :disabled="submit_disabled" color="primary" type="filled">
                                        {{ editingId ? 'Update' : 'Save' }}
                                    </vs-button>
                                </vs-col>
                            </vs-row>
                        </vx-card>
                    </div>
                </vs-tab>
            </vs-tabs>

            <!-- DRP Input Format Dialog -->
            <vs-popup :active.sync="drpDialogActive" title="DRP Input Format" class="drp-input-format-popup" style="min-width: 600px; z-index: 53000;">
                <div>
                    <div class="mb-4">
                        <h4 class="mb-1">Configure DRP Input Fields</h4>
                        <p class="text-muted mb-3">Add or manage the names of the fields required for DRP entry.</p>
                        
                        <!-- Key Table -->
                        <vs-table :data="drpInputFormatArray" slot="no-body" :hoverFlat="true" stripe noDataText="">
                            <vs-tr>
                                <vs-th style="min-width: 200px;">Field Name (Key)</vs-th>
                            </vs-tr>
                            <template v-if="drpInputFormatArray.length > 0">
                                <template v-for="(item, index) in drpInputFormatArray">
                                    <vs-tr :key="item.originalKey">
                                        <vs-td>
                                            <div class="flex items-center">
                                                <vs-input
                                                    v-if="isDefaultKey(item.originalKey)"
                                                    :value="item.key"
                                                    :disabled="true"
                                                    placeholder="Enter field name"
                                                    class="w-full"
                                                />
                                                <vs-input
                                                    v-else
                                                    v-model="newRowKeys[item.originalKey]"
                                                    placeholder="Enter field name"
                                                    class="w-full"
                                                    @blur="finalizeNewKey(item.originalKey)"
                                                />
                                                <vs-button 
                                                    v-if="!isDefaultKey(item.originalKey)"
                                                    @click="removeDRPKey(item.originalKey)" 
                                                    color="danger" 
                                                    type="flat" 
                                                    icon="close" 
                                                    size="small"
                                                    class="ml-2"
                                                ></vs-button>
                                            </div>
                                        </vs-td>
                                    </vs-tr>
                                </template>
                            </template>
                        </vs-table>

                        <!-- Big Add Button -->
                        <div class="mt-4 p-2 text-center border-dashed-custom">
                            <vs-button 
                                @click="addNewDRPRow" 
                                color="success" 
                                type="border" 
                                icon="add" 
                                class="w-full py-3"
                                icon-pack="feather"
                            >
                                <span class="ml-2 font-bold">Add New Input Field</span>
                            </vs-button>
                        </div>
                    </div>
                    
                    <div class="text-center mt-4">
                        <vs-button @click="saveDRPFormat" class="mx-2" color="primary">
                            Save
                        </vs-button>
                        <vs-button @click="cancelDRPDialog" class="mx-2" color="danger">
                            Cancel
                        </vs-button>
                    </div>
                </div>
            </vs-popup>
        </template>
    </div>
</template>

<script>
import { faRedo, faCog, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Request } from '../../utils/requests';
import SelectForeign from '../../components/letstream/ls-select-foreign/SelectForeign.vue';

export default {
    components: {
        SelectForeign
    },
    data() {
        let offset = parseInt(this.$route.query.offset || 0);
        let limit = parseInt(this.$route.query.limit || 20);
        
        let current_page = offset == 0 ? 1 : Math.ceil(offset / limit) + 1;
        return {
            results: [],
            loading: false,
            submit_disabled: false,
            api_errors: {},
            editingId: null,
            total_results: null,
            current_page: current_page,
            itemGroupDescription: '',
            itemGroupMap: {}, // Map to store item group ID to description
            itemGroupCodeMap: {}, // Map to store item group ID to code
            uomMap: {}, // Map to store UOM ID to code
            uomDescriptionMap: {}, // Map to store UOM ID to description
            drpDialogActive: false,
            drpInputFormat: {
                side: "",
                lane: "",
                ch_from: "",
                ch_to: "",
                qty: "",
                remark: ""
            },
            newRowKeys: {}, // Track new row keys separately to prevent re-render issues
            drpInputFormatBackup: {},
            drpSavedFlg: false,
            newDRPKey: '',
            newDRPValue: '',
            sideOptions: [], // Options for side dropdown
            laneOptions: [], // Options for lane dropdown
            activeTab: 0,
            filters: {
                search: this.$route.query.search || null,
                filter_item_group: this.$route.query.filter_item_group ? parseInt(this.$route.query.filter_item_group) : null,
                filter_status: this.$route.query.filter_status || null,
                offset: offset,
                limit: limit
            },
            status_options: [
                { value: null, label: 'All Status' },
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
            ],
            formData: {
                item_group: null,
                code: null,
                description: null,
                uom: null,
                is_active: true
            }
        }
    },
    computed: {
        redoIcon: () => faRedo,
        settingsIcon: () => faCog,
        plusIcon: () => faPlus,
        isAdmin() {
            return this.$store.state.auth.userRole == 'admin' || this.hasAccess('read', 'master_year', 'any');
        },
        canCreate() {
            return this.$store.state.auth.userRole == 'admin' || this.hasAccess('create', 'master_year', 'all');
        },
        canEdit() {
            return this.$store.state.auth.userRole == 'admin' || this.hasAccess('edit', 'master_year', 'all');
        },
        total_pages() {
            if(this.total_results == 0 || this.filters.limit == 0)  return 1
            return Math.ceil(this.total_results/this.filters.limit)
        },
        has_active_filters() {
            return !!(this.filters.search || this.filters.filter_item_group || this.filters.filter_status)
        },
        drpInputFormatArray() {
            // Convert drpInputFormat object to array for table display
            return Object.keys(this.drpInputFormat)
                .sort((a, b) => {
                    const order = ['ch_from', 'ch_to', 'side', 'lane', 'qty', 'remark'];
                    let keyA = !this.isDefaultKey(a) ? (this.newRowKeys[a] || '') : a
                    let keyB = !this.isDefaultKey(b) ? (this.newRowKeys[b] || '') : b
                    
                    let indexA = order.indexOf(keyA);
                    let indexB = order.indexOf(keyB);
                    
                    if (indexA === -1) indexA = 999;
                    if (indexB === -1) indexB = 999;
                    
                    if (indexA !== indexB) return indexA - indexB;
                    return keyA.toLowerCase().localeCompare(keyB.toLowerCase())
                })
                .map(key => {
                    let displayKey = key
                    if (!this.isDefaultKey(key)) {
                        // Show the user-input name from newRowKeys proxy
                        displayKey = this.newRowKeys[key] || ''
                    }
                    return {
                        key: displayKey,
                        originalKey: key, // Keep original key for updates
                        value: this.drpInputFormat[key]
                    }
                })
        },
        itemGroupEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_item_group}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_item_group}?is_active=true`;
        },
        uomEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_uom}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_uom}?is_active=true`;
        },
        sideEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_side}?organisation=${orgId}&is_active=true` : this.$store.state.apiendpoints.master_side;
        }
    },
    watch: {
        filters: {
            deep: true,
            handler(newV) {
                if (this.current_page == null) return;

                if (this.timer) clearTimeout(this.timer);

                this.timer = setTimeout(() => {
                    this.$router.push({ 
                        name: this.$route.name, 
                        query: {
                            offset: newV.offset,
                            limit: newV.limit,
                            search: newV.search || undefined,
                            filter_item_group: newV.filter_item_group || undefined,
                            filter_status: newV.filter_status || undefined
                        } 
                    });
                }, 800);
            }
        },
        "$route.query"(newV) {
            this.loadData();
        },
        current_page(newV, oldV) {
            if (newV == oldV) return;
            this.filters.offset = (newV - 1) * this.filters.limit;
        },
        drpDialogActive(newVal) {
            if (!newVal) {
                // If closed without explicitly clicking 'Save', restore from backup
                if (!this.drpSavedFlg) {
                    this.drpInputFormat = JSON.parse(JSON.stringify(this.drpInputFormatBackup))
                    this.newRowKeys = {}
                    this.newDRPKey = ''
                    this.newDRPValue = ''
                }
                this.drpSavedFlg = false;
            }
        }
    },
    mounted() {
        this.loadItemGroupData()
        this.loadUOMData()
        this.init()
    },
    methods: {
        loadItemGroupData() {
            // Load Item Group master data to create ID to description and code mapping
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            let url = orgId ? `${this.$store.state.apiendpoints.master_item_group}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_item_group}?is_active=true`;
            
            Request(this, 'GET', url, {limit: 1000}).then(
                (res) => {
                    let itemGroupList = [];
                    if (res && res.results && Array.isArray(res.results)) {
                        itemGroupList = res.results;
                    } else if (res && res.data && res.data.results) {
                        itemGroupList = res.data.results;
                    } else if (Array.isArray(res)) {
                        itemGroupList = res;
                    } else if (res && res.data && Array.isArray(res.data)) {
                        itemGroupList = res.data;
                    }
                    
                    // Create maps: itemGroupMap[id] = description, itemGroupCodeMap[id] = code
                    this.itemGroupMap = {};
                    this.itemGroupCodeMap = {};
                    itemGroupList.forEach(item => {
                        if (item.id) {
                            if (item.description) {
                                this.itemGroupMap[item.id] = item.description;
                            }
                            if (item.code) {
                                this.itemGroupCodeMap[item.id] = item.code;
                            }
                        }
                    });
                },
                (err) => {
                    // Silently fail
                }
            )
        },
        loadUOMData() {
            // Load UOM master data to create ID to code mapping
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            let url = orgId ? `${this.$store.state.apiendpoints.master_uom}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_uom}?is_active=true`;
            
            Request(this, 'GET', url, {limit: 1000}).then(
                (res) => {
                    let uomList = [];
                    if (res && res.results && Array.isArray(res.results)) {
                        uomList = res.results;
                    } else if (res && res.data && res.data.results) {
                        uomList = res.data.results;
                    } else if (Array.isArray(res)) {
                        uomList = res;
                    } else if (res && res.data && Array.isArray(res.data)) {
                        uomList = res.data;
                    }
                    
                    // Create maps: uomMap[id] = code, uomDescriptionMap[id] = description
                    this.uomMap = {};
                    this.uomDescriptionMap = {};
                    uomList.forEach(uom => {
                        if (
                            uom &&
                            (uom.is_active === true || uom.is_active === 1 || uom.is_active === '1' || String(uom.is_active).toLowerCase() === 'true') &&
                            uom.id
                        ) {
                            this.uomMap[uom.id] = uom.code || null;
                            this.uomDescriptionMap[uom.id] = uom.description || null;
                        }
                    });
                },
                (err) => {
                    // Silently fail
                }
            )
        },
        updateItemGroupDescription() {
            // Update description when item group is selected
            if (this.formData.item_group && this.itemGroupMap[this.formData.item_group]) {
                this.itemGroupDescription = this.itemGroupMap[this.formData.item_group];
            } else {
                this.itemGroupDescription = '';
            }
        },
        loadData() {
            this.loading = true
            this.$vs.loading()
            
            let url = this.$store.state.apiendpoints.master_item_sub_group
            
            let payload = {
                offset: this.filters.offset,
                limit: this.filters.limit
            }
            if (this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id) {
                payload.organisation = this.$store.state.organisation.organisation.id
            }
            if (this.filters.search) payload.search = this.filters.search
            if (this.filters.filter_item_group) payload.item_group = this.filters.filter_item_group
            if (this.filters.filter_status !== null && this.filters.filter_status !== undefined) payload.is_active = this.filters.filter_status
            
            Request(this, 'GET', url, payload).then(
                (res) => {
                    // Handle response structure: {status: true, data: {count, results: [...]}} or nested
                    if (res && res.data && res.data.results && Array.isArray(res.data.results)) {
                        // Nested response: {status: true, data: {count, results: [...]}}
                        this.results = res.data.results
                        this.total_results = res.data.count || 0
                    } else if (res && res.results && Array.isArray(res.results)) {
                        // Standard paginated response: {count, results: [...]}
                        this.results = res.results
                        this.total_results = res.count || 0
                    } else if (Array.isArray(res)) {
                        // Direct array response
                        this.results = res
                        this.total_results = res.length
                    } else if (res && res.data && Array.isArray(res.data)) {
                        // Nested array: {data: [...]}
                        this.results = res.data
                        this.total_results = res.data.length
                    } else {
                        this.results = []
                        this.total_results = 0
                    }
                    if (this.filters.search) {
                        const q = this.filters.search.toLowerCase()
                        this.results = this.results.filter(item =>
                            (item.code && item.code.toLowerCase().includes(q)) ||
                            (item.description && item.description.toLowerCase().includes(q))
                        )
                        this.total_results = this.results.length
                    }
                    if (this.filters.filter_status !== null && this.filters.filter_status !== undefined) {
                        const activeFilter = this.filters.filter_status === 'true'
                        this.results = this.results.filter(item => Boolean(item.is_active) === activeFilter)
                        this.total_results = this.results.length
                    }
                    this.calculate_page()
                    this.loading = false
                    this.$vs.loading.close()
                },
                (err) => {
                    this.loading = false
                    this.$vs.loading.close()
                    this.$vs.notify({
                        time: 3000,
                        title: 'Error',
                        text: "An error occurred while loading data. Please try again!",
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger'
                    })
                }
            )
        },
        save() {
            if (!this.formData.item_group || !this.formData.code || !this.formData.description || !this.formData.uom) {
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Please fill all required fields!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                })
                return
            }
            
            if (!this.$store.state.organisation.organisation || !this.$store.state.organisation.organisation.id) {
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Organisation not found!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                })
                return
            }
            
            this.submit_disabled = true
            this.$vs.loading()
            
            let url = this.$store.state.apiendpoints.master_item_sub_group
            let method = 'POST'
            
            if (this.editingId) {
                url += this.editingId + '/'
                method = 'PUT'
            }
            
            let payload = {
                item_group: parseInt(this.formData.item_group) || this.formData.item_group,
                code: this.formData.code,
                description: this.formData.description,
                uom: parseInt(this.formData.uom) || this.formData.uom,
                drp_input_format: { ...this.drpInputFormat },
                is_active: this.formData.is_active,
                organisation: this.$store.state.organisation.organisation.id
            }
            
            Request(this, method, url, null, payload).then(
                (res) => {
                    this.submit_disabled = false
                    this.$vs.loading.close()
                    this.$vs.notify({
                        time: 3000,
                        title: 'Success',
                        text: this.editingId ? "Updated Successfully!" : "Saved Successfully!",
                        iconPack: 'feather',
                        icon: 'icon-check-circle',
                        color: 'success'
                    })
                    this.resetForm()
                    this.loadData()
                    this.activeTab = 0
                },
                (err) => {
                    this.submit_disabled = false
                    this.$vs.loading.close()
                    if (err.error) {
                        this.api_errors = err.error
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "Please fix the errors and try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    } else {
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "An unknown error occurred! Please try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    }
                }
            )
        },
        editItem(item) {
            this.editingId = item.id
            
            // Handle both ID and object for foreign keys
            const itemGroupId = (typeof item.item_group === 'object' && item.item_group) ? item.item_group.id : item.item_group
            const uomId = (typeof item.uom === 'object' && item.uom) ? item.uom.id : item.uom
            
            this.formData = {
                item_group: itemGroupId,
                code: item.code,
                description: item.description,
                uom: uomId,
                is_active: item.is_active
            }
            
            // Populate DRP input format
            if (item.drp_input_format) {
                this.drpInputFormat = JSON.parse(JSON.stringify(item.drp_input_format))
            } else {
                this.drpInputFormat = {
                    side: "",
                    lane: "",
                    ch_from: "",
                    ch_to: "",
                    qty: "",
                    remark: ""
                }
            }
            
            // Update description based on selected group
            this.updateItemGroupDescription()
            
            this.activeTab = 1
            // Scroll to top
            if (this.$el) {
                this.$el.scrollIntoView({ behavior: 'smooth' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
        resetForm() {
            this.editingId = null
            this.formData = {
                item_group: null,
                code: null,
                description: null,
                uom: null,
                is_active: true
            }
            this.itemGroupDescription = ''
            this.drpInputFormat = {
                side: "",
                lane: "",
                ch_from: "",
                ch_to: "",
                qty: "",
                remark: ""
            }
            this.newRowKeys = {}
            this.newDRPKey = ''
            this.newDRPValue = ''
            this.api_errors = {}
            this.generateCode()

            // Scroll to top
            if (this.$el) {
                this.$el.scrollIntoView({ behavior: 'smooth' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
        async openDRPDialog() {
            // Backup current format before opening dialog
            this.drpInputFormatBackup = JSON.parse(JSON.stringify(this.drpInputFormat))
            
            // Reset newRowKeys tracking and initialize with existing custom keys
            this.newRowKeys = {}
            Object.keys(this.drpInputFormat).forEach(key => {
                if (!this.isDefaultKey(key)) {
                    this.$set(this.newRowKeys, key, key)
                }
            })
            
            this.drpSavedFlg = false
            // Load side and lane options for dropdowns
            await this.loadSideOptions()
            await this.loadLaneOptions()
            
            // Open dialog
            this.drpDialogActive = true
        },
        async loadSideOptions() {
            // Load side master data for dropdown
            try {
                let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
                let url = orgId ? `${this.$store.state.apiendpoints.master_side}?organisation=${orgId}&is_active=true` : this.$store.state.apiendpoints.master_side;
                
                const res = await Request(this, 'GET', url, {limit: 1000});
                let sideList = [];
                if (res && res.results && Array.isArray(res.results)) {
                    sideList = res.results;
                } else if (res && res.data && res.data.results) {
                    sideList = res.data.results;
                } else if (Array.isArray(res)) {
                    sideList = res;
                } else if (res && res.data && Array.isArray(res.data)) {
                    sideList = res.data;
                }
                
                this.sideOptions = sideList;
            } catch (err) {
                this.sideOptions = [];
            }
        },
        async loadLaneOptions() {
            // Load lane master data for dropdown
            try {
                let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
                let url = orgId ? `${this.$store.state.apiendpoints.master_lane}?organisation=${orgId}&is_active=true` : this.$store.state.apiendpoints.master_lane;
                
                const res = await Request(this, 'GET', url, {limit: 1000});
                let laneList = [];
                if (res && res.results && Array.isArray(res.results)) {
                    laneList = res.results;
                } else if (res && res.data && res.data.results) {
                    laneList = res.data.results;
                } else if (Array.isArray(res)) {
                    laneList = res;
                } else if (res && res.data && Array.isArray(res.data)) {
                    laneList = res.data;
                }
                
                this.laneOptions = laneList;
            } catch (err) {
                this.laneOptions = [];
            }
        },
        cancelDRPDialog() {
            // Restore from backup if cancelled
            this.drpInputFormat = JSON.parse(JSON.stringify(this.drpInputFormatBackup))
            this.newRowKeys = {} // Clear new row keys tracking
            this.newDRPKey = ''
            this.newDRPValue = ''
            this.drpDialogActive = false
        },
        saveDRPFormat() {
            // Finalize all custom names before saving
            const keysToProcess = Object.keys(this.newRowKeys)
            keysToProcess.forEach(tempKey => {
                const newKeyName = this.newRowKeys[tempKey]
                if (newKeyName && newKeyName.trim() !== '') {
                    const trimmedKey = newKeyName.trim()
                    // If key name changed, move the value and delete old key
                    if (trimmedKey !== tempKey) {
                        // Check if key doesn't already exist (to avoid overwriting defaults or other custom fields)
                        if (!this.drpInputFormat.hasOwnProperty(trimmedKey)) {
                            const value = this.drpInputFormat[tempKey]
                            this.$delete(this.drpInputFormat, tempKey)
                            this.$set(this.drpInputFormat, trimmedKey, value)
                        } else if (tempKey.startsWith('new_key_')) {
                            // If it was a new temp row but key exists, we can't merge easily here, 
                            // but usually the blur prevents this. For now, just skip if duplicate.
                        }
                    }
                } else if (tempKey.startsWith('new_key_')) {
                    // Remove temporary keys with no name
                    this.$delete(this.drpInputFormat, tempKey)
                }
                this.$delete(this.newRowKeys, tempKey)
            })
            
            this.newDRPKey = ''
            this.newDRPValue = ''
            this.drpSavedFlg = true
            this.drpDialogActive = false
        },
        addNewDRPRow() {
            // Add a new empty row with a temporary unique key (both key and value will be empty)
            const tempKey = `new_key_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            this.$set(this.drpInputFormat, tempKey, "")
            // Track the key input separately to avoid re-render issues
            this.$set(this.newRowKeys, tempKey, "")
        },
        finalizeNewKey(originalKey) {
            // Called when user finishes typing the key (on blur)
            const newKeyName = this.newRowKeys[originalKey]
            if (!newKeyName || newKeyName.trim() === '') {
                // If key is empty, keep the temporary key
                return
            }
            const trimmedKey = newKeyName.trim()
            // Check if new key already exists (and it's not the same key)
            if (this.drpInputFormat.hasOwnProperty(trimmedKey) && trimmedKey !== originalKey) {
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Key already exists!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                })
                return
            }
            // Move value from old key to new key
            const value = this.drpInputFormat[originalKey]
            this.$delete(this.drpInputFormat, originalKey)
            this.$set(this.drpInputFormat, trimmedKey, value)
            // Update newRowKeys tracking
            this.$delete(this.newRowKeys, originalKey)
            this.$set(this.newRowKeys, trimmedKey, trimmedKey)
        },
        addDRPKeyValue() {
            if (this.newDRPKey && this.newDRPKey.trim() !== '') {
                // Check if key already exists
                if (this.drpInputFormat.hasOwnProperty(this.newDRPKey.trim())) {
                    this.$vs.notify({
                        time: 3000,
                        title: 'Error',
                        text: "Key already exists!",
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger'
                    })
                    return
                }
                // Add new key-value pair
                this.$set(this.drpInputFormat, this.newDRPKey.trim(), this.newDRPValue || "")
                this.newDRPKey = ''
                this.newDRPValue = ''
            }
        },
        removeDRPKey(key) {
            if (!this.isDefaultKey(key)) {
                this.$delete(this.drpInputFormat, key)
                // Also remove from newRowKeys if it exists
                if (this.newRowKeys.hasOwnProperty(key)) {
                    this.$delete(this.newRowKeys, key)
                }
            }
        },
        updateDRPValue(key, event) {
            this.$set(this.drpInputFormat, key, event.target.value)
        },
        isDefaultKey(key) {
            const defaultKeys = ['side', 'lane', 'ch_from', 'ch_to', 'qty', 'remark']
            return defaultKeys.includes(key)
        },
        calculate_page() {
            if (this.filters.offset == 0) {
                this.current_page = 1;
            } else {
                this.current_page = Math.ceil(this.filters.offset / this.filters.limit) + 1;
            }
        },
        getItemGroupCode(itemGroupId) {
            // Handle both ID (number) and nested object
            if (typeof itemGroupId === 'object' && itemGroupId && itemGroupId.code) {
                return itemGroupId.code;
            } else if (itemGroupId && this.itemGroupCodeMap[itemGroupId]) {
                return this.itemGroupCodeMap[itemGroupId];
            }
            return null;
        },
        getItemGroupDescription(itemGroupId) {
            // Handle both ID (number) and nested object
            if (typeof itemGroupId === 'object' && itemGroupId && itemGroupId.description) {
                return itemGroupId.description;
            } else if (itemGroupId && this.itemGroupMap[itemGroupId]) {
                return this.itemGroupMap[itemGroupId];
            }
            return null;
        },
        getUOMCode(uomId) {
            // Handle both ID (number) and nested object
            if (typeof uomId === 'object' && uomId && uomId.code) {
                return uomId.code;
            } else if (uomId && this.uomMap[uomId]) {
                return this.uomMap[uomId];
            }
            return null;
        },
        getUOMDescription(uomId) {
            // Handle both ID (number) and nested object
            if (typeof uomId === 'object' && uomId && uomId.description) {
                return uomId.description;
            } else if (uomId && this.uomDescriptionMap[uomId]) {
                return this.uomDescriptionMap[uomId];
            }
            return null;
        },
        init() {
            let offset = parseInt(this.$route.query.offset || 0);
            let limit = parseInt(this.$route.query.limit || 20);
            this.filters.offset = offset;
            this.filters.limit = limit;
            this.filters.search = this.$route.query.search || null;
            this.filters.filter_item_group = this.$route.query.filter_item_group ? parseInt(this.$route.query.filter_item_group) : null;
            this.filters.filter_status = this.$route.query.filter_status || null;
            this.calculate_page();
            
            if (!this.$route.query.offset || !this.$route.query.limit) {
                this.$router.replace({ 
                    name: this.$route.name, 
                    query: { 
                        ...this.$route.query,
                        offset: this.filters.offset, 
                        limit: this.filters.limit 
                    } 
                });
            }
            
            this.loadData();
            this.generateCode();
        },
        generateCode() {
            if (this.editingId) return

            let url = this.$store.state.apiendpoints.master_item_sub_group
            let payload = {
                limit: 1000
            }
            if (this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id) {
                payload.organisation = this.$store.state.organisation.organisation.id
            }
            
            Request(this, 'GET', url, payload).then(
                (res) => {
                    let items = []
                    // Handle various response formats
                    if (res && res.data && res.data.results) {
                        items = res.data.results
                    } else if (res && res.results && Array.isArray(res.results)) {
                        items = res.results
                    } else if (Array.isArray(res)) {
                        items = res
                    } else if (res && res.data && Array.isArray(res.data)) {
                        items = res.data
                    }

                    let maxNum = 0
                    items.forEach(item => {
                        if (item.code && item.code.startsWith('S')) {
                            let numStr = item.code.replace(/^\D+/, '')
                            let num = parseInt(numStr)
                            if (!isNaN(num) && num > maxNum) {
                                maxNum = num
                            }
                        }
                    })

                    let nextNum = maxNum + 1
                    this.formData.code = 'S' + nextNum.toString().padStart(3, '0')
                }
            )
        }
    }
}
</script>

<style lang="scss" scoped>
.text-muted {
    color: #6c757d;
    font-size: 0.9rem;
}

.text-success {
    color: #28a745;
    font-weight: 500;
}

.text-danger {
    color: #dc3545;
    font-weight: 500;
}

.text-warning {
    color: #FF9F43;
    font-weight: 500;
}

.mb-4 {
    margin-bottom: 1.5rem;
}

.mb-2 {
    margin-bottom: 0.5rem;
}

.mt-4 {
    margin-top: 1.5rem;
}

// Fix z-index for dropdown in popup
.drp-input-format-popup {
        /* Ensure popup appears above everything */
        ::v-deep .vs-popup {
            z-index: 53000 !important;
        }

        /* Limit popup height to viewport and enable internal scrolling */
        ::v-deep .vs-popup--content {
            position: relative;
            z-index: 53000 !important;
            max-height: calc(100vh - 120px) !important; /* leaves space for header/footer */
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch; /* smooth scrolling on mobile */
        }

        /* Make table and cells allow overflow for dropdowns while keeping scroll inside popup */
        ::v-deep .vs-table--tbody {
            overflow: visible !important;
        }

        ::v-deep .vs-table--tr {
            overflow: visible !important;
        }

        ::v-deep .vs-table--td {
            overflow: visible !important;
            position: relative;
        }

        /* Ensure the popup header stays visible and the close button doesn't overlap content */
        ::v-deep .vs-popup__header {
            position: sticky;
            top: 0;
            background: #fff;
            z-index: 53010 !important;
        }

        ::v-deep .vs-popup__close {
            z-index: 53020 !important;
        }
    // Style for side select dropdown
    .side-select {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid rgba(0, 0, 0, 0.2);
        border-radius: 5px;
        background: white;
        font-size: 14px;
        color: #626262;
        cursor: pointer;
        appearance: menulist;
        -webkit-appearance: menulist;
        -moz-appearance: menulist;
        
        &:focus {
            outline: none;
            border-color: rgba(var(--vs-primary), 1);
        }
        
        option {
            padding: 10px;
        }
    }

    .border-dashed-custom {
        border: 2px dashed rgba(0,0,0,0.1);
        border-radius: 8px;
        background: #fcfcfc;
        
        .vs-button {
            padding: 15px !important;
            font-size: 1.1rem;
        }
    }
}
</style>


